<div class="d-grid gap-3">
    <div class="card profile-hero-card p-4 mb-4 overflow-hidden">
        <div class="row g-4 align-items-stretch">
            <div class="col-lg-8 d-flex align-items-center gap-4 flex-column flex-md-row">
                <span class="settings-section-icon d-inline-flex align-items-center justify-content-center flex-shrink-0 rounded-4 text-white">
                        <i class="bi bi-people-fill"></i>
                </span>

                <div class="d-flex flex-column gap-3">
                    <span class="badge rounded-pill profile-kicker align-self-start fw-bold text-uppercase">Workspace access</span>
                    <h2 class="text-white mb-0 profile-hero-name">User Management</h2>
                    <p class="text-white-50 mb-0 profile-hero-meta">
                        Add users, update their roles, and manage account access.
                    </p>
                    <div class="d-flex flex-wrap gap-2">
                        <span class="badge rounded-pill profile-meta-pill d-inline-flex align-items-center px-3 py-2 fw-semibold">
                            <i class="bi bi-shield-check me-2"></i>{{ role_options.length || 0 }} role{{ role_options.length === 1 ? "" : "s" }}
                        </span>
                        <span class="badge rounded-pill profile-meta-pill d-inline-flex align-items-center px-3 py-2 fw-semibold">
                            <i class="bi bi-person-check me-2"></i>{{ active_users_count }} active
                        </span>
                    </div>
                </div>
            </div>

            <div class="col-lg-4 d-flex flex-column justify-content-between gap-3">
                <div class="row g-3">
                    <div class="col-6">
                        <div class="card settings-stat-tile h-100 p-3 rounded-4 border text-white d-flex flex-column justify-content-center">
                            <span class="settings-stat-value d-block fw-bold lh-1 mb-2">{{ users.length }}</span>
                            <span class="settings-stat-label d-block text-uppercase fw-bold">Total users</span>
                        </div>
                    </div>
                    <div class="col-6">
                        <div class="card settings-stat-tile h-100 p-3 rounded-4 border text-white d-flex flex-column justify-content-center">
                            <span class="settings-stat-value d-block fw-bold lh-1 mb-2">{{ active_users_count }}</span>
                            <span class="settings-stat-label d-block text-uppercase fw-bold">Active</span>
                        </div>
                    </div>
                </div>

                <button
                    type="button"
                    class="btn btn-sm py-2 text-xs btn-primary profile-edit-btn settings-add-user-trigger w-100 border-0 text-white"
                    @click="open_add_user"
                >
                    <i class="bi bi-person-plus-fill me-2"></i>
                    <span>Add user</span>
                </button>
            </div>
        </div>
    </div>

    <div v-if="load_error" class="alert alert-danger border-0 rounded-3 mb-3" role="alert">
        {{ load_error }}
    </div>

    <div v-if="save_success" class="alert alert-success border-0 rounded-3 mb-3" role="alert">
        {{ save_success }}
    </div>

    <div class="card settings-table-shell profile-surface-card p-4 rounded-4 overflow-hidden">
        <div v-if="is_loading" class="d-flex align-items-center gap-3 text-white-50 p-3">
            <div class="spinner-border spinner-border-sm text-light" role="status" aria-hidden="true"></div>
            <span class="text-xs">Loading users...</span>
        </div>

        <div v-else-if="!users.length" class="text-center text-white py-4 text-xs">
            <div class="d-flex flex-column align-items-center justify-content-center py-4">
                <span class="settings-empty-icon d-inline-flex align-items-center justify-content-center mb-3 rounded-4 text-white">
                    <i class="bi bi-people-fill"></i>
                </span>
                <p class="text-white fw-semibold mb-1">No users available</p>
                <p class="text-white text-xs mb-0">Added users will appear here.</p>
            </div>
        </div>

        <div v-else class="table-responsive">
            <table
                class="table table-dark table-hover align-middle mb-0 w-100 settings-data-table global_data_table text-xs"
                data-search-placeholder="Search users"
                data-non-orderable-targets="[7]"
                data-page-length="5"
                data-order-column="0"
                data-order-direction="asc"
            >
                <thead>
                    <tr>
                        <th scope="col" class="settings-table-head fw-semibold text-xs text-uppercase">User</th>
                        <th scope="col" class="settings-table-head fw-semibold text-xs text-uppercase">Email</th>
                        <th scope="col" class="settings-table-head fw-semibold text-xs text-uppercase">Phone</th>
                        <th scope="col" class="settings-table-head fw-semibold text-xs text-uppercase">Role</th>
                        <th scope="col" class="settings-table-head fw-semibold text-xs text-uppercase">Status</th>
                        <th scope="col" class="settings-table-head fw-semibold text-xs text-uppercase">Joined</th>
                        <th scope="col" class="settings-table-head fw-semibold text-xs text-uppercase">Last Login</th>
                        <th scope="col" class="settings-table-head fw-semibold text-xs text-end text-uppercase">Actions</th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="user in users" :key="user.id">
                        <td data-label="Member">
                            <div class="d-flex align-items-center gap-2">
                                <span class="settings-user-avatar d-inline-flex align-items-center justify-content-center rounded-circle text-white fw-bold">
                                    {{ get_initials(user) }}
                                </span>
                                <div class="d-flex flex-column gap-1">
                                    <span class="text-white fw-semibold text-xs">{{ get_full_name(user) }}</span>
                                    <span class="settings-muted-cell settings-user-username">{{ user.username || "No username" }}</span>
                                    <span class="settings-muted-cell settings-user-email-mobile d-md-none">{{ user.email || "-" }}</span>
                                </div>
                            </div>
                        </td>
                        <td data-label="Email" class="text-white fw-semibold text-xs settings-email-cell">{{ user.email || "-" }}</td>
                        <td data-label="Phone" class="text-xs text-nowrap" :class="user.phone_number ? 'text-white' : 'text-white-50'">
                            {{ user.phone_number || "-" }}
                        </td>
                        <td data-label="Role">
                            <span class="badge rounded-pill settings-soft-badge text-xs d-inline-flex align-items-center justify-content-center fw-bold">
                                {{ user.role || "-" }}
                            </span>
                        </td>
                        <td data-label="Status">
                            <span
                                class="badge rounded-pill settings-status-pill text-xs d-inline-flex align-items-center justify-content-center fw-bold"
                                :class="user.is_active ? 'settings-status-active' : 'settings-status-inactive'"
                            >
                                {{ user.is_active ? "Active" : "Inactive" }}
                            </span>
                        </td>
                        <td data-label="Joined" :data-order="get_date_order(user.date_joined)" class="text-xs text-nowrap" :class="user.date_joined ? 'text-white' : 'text-white-50'">
                            {{ user.date_joined ? format_user_date(user.date_joined) : "-" }}
                        </td>
                        <td data-label="Last Login" :data-order="get_date_order(user.last_login)" class="text-xs text-nowrap" :class="user.last_login ? 'text-white' : 'text-white-50'">
                            {{ format_user_date(user.last_login, true) }}
                        </td>
                        <td data-label="Actions">
                            <div class="d-flex justify-content-end gap-2 flex-wrap">
                                <button
                                    type="button"
                                    class="btn btn-sm settings-icon-action d-inline-flex align-items-center justify-content-center"
                                    @click="open_edit_user(user)"
                                    aria-label="Edit user"
                                    title="Edit user"
                                >
                                    <i class="bi bi-pencil-square"></i>
                                </button>

                                <button
                                    type="button"
                                    class="btn btn-sm settings-action-button d-inline-flex align-items-center justify-content-center fw-bold px-3"
                                    @click="toggle_user_status(user)"
                                    :disabled="is_saving || (is_current_user(user) && user.is_active)"
                                    :aria-label="user.is_active ? 'Deactivate user' : 'Activate user'"
                                    :title="is_current_user(user) && user.is_active ? 'You cannot deactivate your own account' : ''"
                                >
                                    <span class="text-xs">{{ user.is_active ? "Deactivate" : "Activate" }}</span>
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <SettingsAddUserModal
        :is_open="show_add_user_modal"
        :is_saving="is_saving"
        :form_error="form_error"
        :role_options="role_options"
        :is_loading_roles="is_loading_roles"
        @close="close_add_user_modal"
        @submit="add_user"
    />

</div>
